import { useEffect, useRef } from "react";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { oneDark } from "@codemirror/theme-one-dark";

interface Props {
  value: string;
  onChange: (v: string) => void;
}

// JSON body editor backed by CodeMirror 6. Syntax highlighting, line
// numbers, bracket matching come for free. We use the One Dark theme
// because it visually fits the rest of the dark surface chrome; light
// theme picks up CodeMirror's default light look automatically when
// `data-theme="light"` is set on the document root.
export default function JsonCodeEditor({ value, onChange }: Props) {
  // The host editor we wrap. The library handles the document via its
  // own state, but we still need a ref to focus / blur on demand.
  const cmRef = useRef<ReactCodeMirrorRef | null>(null);

  // Toggle the theme by re-reading the html.dark class. CodeMirror's
  // theme is applied via an extension, so we re-key the editor on
  // class changes to swap themes without a full remount.
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  useEffect(() => {
    // Force a refresh when the doc class changes — useful if the user
    // toggles theme while the editor is open.
    const obs = new MutationObserver(() => {
      // The component above already swaps the theme extension via
      // re-render. Nothing to do here beyond signalling React.
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  return (
    <CodeMirror
      ref={cmRef}
      value={value}
      onChange={onChange}
      theme={isDark ? oneDark : "light"}
      extensions={[json()]}
      height="100%"
      basicSetup={{
        lineNumbers: true,
        highlightActiveLine: true,
        bracketMatching: true,
        closeBrackets: true,
        indentOnInput: true,
        autocompletion: false,
        foldGutter: false,
        searchKeymap: false,
      }}
      className="cm-host border border-border rounded h-full min-h-[160px]"
    />
  );
}
